import * as RCheckbox from '@radix-ui/react-checkbox'
import * as RRadio from '@radix-ui/react-radio-group'
import * as RSwitch from '@radix-ui/react-switch'
import { Check, Minus } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { focusRing } from '../lib/focus'
import { useDomId } from '../lib/useId'

export interface CheckboxProps {
  checked?: boolean | 'indeterminate'
  defaultChecked?: boolean
  onCheckedChange?: (checked: boolean) => void
  disabled?: boolean
  name?: string
  value?: string
  id?: string
  label: ReactNode
  description?: ReactNode
  className?: string
}

export function Checkbox({
  checked,
  defaultChecked,
  onCheckedChange,
  disabled,
  name,
  value,
  id,
  label,
  description,
  className,
}: CheckboxProps) {
  const controlId = useDomId('checkbox', id)
  const descId = description ? `${controlId}-desc` : undefined
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <RCheckbox.Root
        id={controlId}
        checked={checked}
        defaultChecked={defaultChecked}
        onCheckedChange={(next) => onCheckedChange?.(next === true)}
        disabled={disabled}
        name={name}
        value={value}
        aria-describedby={descId}
        className={cn(
          'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-sm border border-border-control',
          'bg-surface-inset data-[state=checked]:border-brand-solid data-[state=checked]:bg-brand-solid',
          'data-[state=indeterminate]:border-brand-solid data-[state=indeterminate]:bg-brand-solid',
          'disabled:cursor-not-allowed disabled:opacity-50',
          focusRing,
        )}
      >
        <RCheckbox.Indicator className="text-text-on-brand">
          {checked === 'indeterminate' ? (
            <Minus aria-hidden className="size-3" />
          ) : (
            <Check aria-hidden className="size-3" />
          )}
        </RCheckbox.Indicator>
      </RCheckbox.Root>
      <div className="flex flex-col gap-0.5">
        <label htmlFor={controlId} className="cursor-pointer text-body-sm text-text-primary">
          {label}
        </label>
        {description && (
          <p id={descId} className="text-caption text-text-secondary">
            {description}
          </p>
        )}
      </div>
    </div>
  )
}

export interface RadioOption {
  value: string
  label: ReactNode
  description?: ReactNode
  disabled?: boolean
}

export function RadioGroup({
  options,
  value,
  defaultValue,
  onValueChange,
  name,
  label,
  orientation = 'vertical',
  className,
}: {
  options: RadioOption[]
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  name?: string
  label: string
  orientation?: 'vertical' | 'horizontal'
  className?: string
}) {
  return (
    <RRadio.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      name={name}
      aria-label={label}
      orientation={orientation}
      className={cn('flex gap-3', orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap', className)}
    >
      {options.map((option) => {
        const id = `radio-${name ?? label}-${option.value}`.replace(/\s+/g, '-')
        return (
          <div key={option.value} className="flex items-start gap-2.5">
            <RRadio.Item
              id={id}
              value={option.value}
              disabled={option.disabled}
              className={cn(
                'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-border-control',
                'bg-surface-inset data-[state=checked]:border-brand-solid',
                'disabled:cursor-not-allowed disabled:opacity-50',
                focusRing,
              )}
            >
              <RRadio.Indicator className="size-2 rounded-full bg-brand-solid" />
            </RRadio.Item>
            <div className="flex flex-col gap-0.5">
              <label htmlFor={id} className="cursor-pointer text-body-sm text-text-primary">
                {option.label}
              </label>
              {option.description && (
                <p className="text-caption text-text-secondary">{option.description}</p>
              )}
            </div>
          </div>
        )
      })}
    </RRadio.Root>
  )
}

export function Switch({
  checked,
  defaultChecked,
  onCheckedChange,
  disabled,
  label,
  hideLabel = false,
  id,
  className,
}: {
  checked?: boolean
  defaultChecked?: boolean
  onCheckedChange?: (checked: boolean) => void
  disabled?: boolean
  label: string
  hideLabel?: boolean
  id?: string
  className?: string
}) {
  const controlId = useDomId('switch', id)
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <RSwitch.Root
        id={controlId}
        checked={checked}
        defaultChecked={defaultChecked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full border border-border-control bg-surface-inset',
          'transition-colors duration-fast data-[state=checked]:border-brand-solid data-[state=checked]:bg-brand-solid',
          'disabled:cursor-not-allowed disabled:opacity-50',
          focusRing,
        )}
      >
        <RSwitch.Thumb className="block size-4 translate-x-0.5 rounded-full bg-text-primary transition-transform duration-fast data-[state=checked]:translate-x-4 data-[state=checked]:bg-text-on-brand" />
      </RSwitch.Root>
      <label htmlFor={controlId} className={cn('text-body-sm text-text-primary', hideLabel && 'sr-only')}>
        {label}
      </label>
    </div>
  )
}
